import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

export interface Ga4TrendPoint {
  snapshot_date: string
  sessions: number
  active_users: number
  page_views: number
  bounce_rate: number
  avg_session_duration: number
}

/** Daily GA4 data for TrendsTab */
export function useGa4Trends(siteId: string, days = 180) {
  return useQuery({
    queryKey: ['trends', 'ga4', siteId, days],
    queryFn: () => apiClient.get<{
      trends: Ga4TrendPoint[]
    }>(`/api/sites/${siteId}/trends/ga4`, { params: { days } }),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  })
}

/** Trigger immediate GA4 snapshot */
export function useTakeGa4Snapshot(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post<{ ok: boolean; rowsStored: number }>(`/api/sites/${siteId}/ga4/snapshot`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trends', 'ga4', siteId] })
    },
  })
}

/** Trigger GA4 backfill (~60 days) */
export function useGa4Backfill(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post<{ ok: boolean; totalRows: number }>(`/api/sites/${siteId}/ga4/backfill`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trends', 'ga4', siteId] })
    },
  })
}

/** Enable daily GA4 snapshots */
export function useEnableGa4Snapshots(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post(`/api/sites/${siteId}/ga4/snapshot/enable`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['site', siteId] })
    },
  })
}
